/**
 * The selected element, and what can be said about it.
 *
 * Everything here is available to both sides. The only asymmetry on the whole
 * board is who may manage the map itself — rename it, share it, delete it —
 * and none of that is here.
 */

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
    CHILD_TYPES,
    COLOUR_INK,
    ELEMENT_COLOURS,
    EMOTIONAL_TYPES,
    ENERGY_DIRECTIONS,
    LINK_STRENGTHS,
    MAX_NAME_LENGTH,
    MAX_NOTE_LENGTH,
    MAX_YEAR,
    MIN_YEAR,
    PERSON_GENDERS,
    UNION_TYPES,
    isNote,
    isPerson,
    isSystem,
    type ChildType,
    type ElementColour,
    type EmotionalType,
    type EnergyDirection,
    type LinkStrength,
    type Household,
    type MapLink,
    type MapNode,
    type NoteNode,
    type PersonNode,
    type SystemNode,
    type UnionType,
} from "../../domain/types";

interface Props {
    node: MapNode | null;
    link: MapLink | null;
    onChangeNode: (node: MapNode) => void;
    onChangeLink: (link: MapLink) => void;
    onRemove: () => void;
    /** The home the selected person lives in, when they live in one. */
    household?: Household | null;
    /** How many people are in it, for the count beside its name. */
    onChangeHousehold?: (household: Household) => void;
    onLeaveHousehold?: () => void;
    editable?: boolean;
}

export default function Inspector({
    node,
    link,
    onChangeNode,
    onChangeLink,
    onRemove,
    household = null,
    onChangeHousehold,
    onLeaveHousehold,
    editable = true,
}: Props) {
    const { t } = useTranslation();

    if (!node && !link) {
        return (
            <p className="text-sm text-content-secondary">
                {t("systemicMap.inspector.empty")}
            </p>
        );
    }

    return (
        <section aria-labelledby="systemic-map-inspector" className="space-y-4">
            <h3
                id="systemic-map-inspector"
                className="text-xs font-semibold uppercase tracking-[0.08em] text-content-secondary"
            >
                {t("systemicMap.inspector.title")}
            </h3>

            {node && isPerson(node) && (
                <PersonFields
                    person={node}
                    editable={editable}
                    onChange={(next) => onChangeNode(next)}
                />
            )}
            {node && isSystem(node) && (
                <SystemFields
                    system={node}
                    editable={editable}
                    onChange={(next) => onChangeNode(next)}
                />
            )}
            {node && isNote(node) && (
                <NoteFields
                    note={node}
                    editable={editable}
                    onChange={(next) => onChangeNode(next)}
                />
            )}
            {node && !isPerson(node) && !isSystem(node) && !isNote(node) && (
                <Field label={t("systemicMap.inspector.centreLabel")}>
                    <input
                        type="text"
                        value={node.label}
                        maxLength={MAX_NAME_LENGTH}
                        disabled={!editable}
                        onChange={(event) =>
                            onChangeNode({ ...node, label: event.target.value })
                        }
                        className={inputClass}
                    />
                </Field>
            )}

            {node && isPerson(node) && household && (
                <HouseholdFields
                    household={household}
                    editable={editable}
                    onChange={onChangeHousehold}
                    onLeave={onLeaveHousehold}
                />
            )}

            {link && (
                <LinkFields
                    link={link}
                    editable={editable}
                    onChange={onChangeLink}
                />
            )}

            {editable && (node || link) && (
                <ColourPicker
                    value={(node ?? link)!.colour ?? "default"}
                    onChange={(colour) =>
                        node
                            ? onChangeNode({ ...node, colour })
                            : onChangeLink({ ...link!, colour })
                    }
                />
            )}

            {editable && (
                <button
                    type="button"
                    onClick={onRemove}
                    className="min-h-11 w-full rounded-lg border border-status-critical/40 px-4 py-2.5 text-sm font-medium text-status-critical transition-colors duration-fast hover:bg-status-critical/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                >
                    {t("systemicMap.inspector.remove")}
                </button>
            )}
        </section>
    );
}

const inputClass =
    "min-h-11 w-full rounded-md border border-border-strong bg-surface-raised px-3 py-2 text-sm text-content-primary placeholder:text-content-tertiary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:opacity-60";

function PersonFields({
    person,
    editable,
    onChange,
}: {
    person: PersonNode;
    editable: boolean;
    onChange: (person: PersonNode) => void;
}) {
    const { t } = useTranslation();

    return (
        <div className="space-y-3">
            <Field label={t("systemicMap.inspector.name")}>
                <input
                    type="text"
                    value={person.name}
                    maxLength={MAX_NAME_LENGTH}
                    disabled={!editable}
                    onChange={(event) => onChange({ ...person, name: event.target.value })}
                    className={inputClass}
                />
            </Field>

            <Field label={t("systemicMap.inspector.gender")}>
                <select
                    value={person.gender}
                    disabled={!editable}
                    onChange={(event) =>
                        onChange({
                            ...person,
                            gender: event.target.value as PersonNode["gender"],
                        })
                    }
                    className={inputClass}
                >
                    {PERSON_GENDERS.map((gender) => (
                        <option key={gender} value={gender}>
                            {t(`systemicMap.gender.${gender}`)}
                        </option>
                    ))}
                </select>
            </Field>

            <div className="grid grid-cols-2 gap-3">
                <YearField
                    label={t("systemicMap.inspector.birthYear")}
                    value={person.birthYear}
                    disabled={!editable}
                    onCommit={(birthYear) => onChange({ ...person, birthYear })}
                />
                <YearField
                    label={t("systemicMap.inspector.deathYear")}
                    value={person.deathYear}
                    disabled={!editable}
                    onCommit={(deathYear) =>
                        // A year and an unticked box would disagree on the
                        // board, where the cross is the same statement.
                        onChange({
                            ...person,
                            deathYear,
                            deceased: deathYear !== null || person.deceased,
                        })
                    }
                />
            </div>

            <Checkbox
                label={t("systemicMap.person.deceased")}
                checked={person.deceased}
                disabled={!editable}
                onChange={(deceased) => onChange({ ...person, deceased })}
            />
            <Checkbox
                label={t("systemicMap.person.indexPatient")}
                checked={person.index}
                disabled={!editable}
                onChange={(index) => onChange({ ...person, index })}
            />

            <Field label={t("systemicMap.inspector.notes")}>
                <textarea
                    value={person.notes}
                    maxLength={MAX_NOTE_LENGTH}
                    rows={3}
                    disabled={!editable}
                    onChange={(event) => onChange({ ...person, notes: event.target.value })}
                    className={`${inputClass} min-h-[88px]`}
                />
            </Field>
        </div>
    );
}

/**
 * The therapist's own colour on this element.
 *
 * A fixed palette rather than a colour wheel: the board is read in two themes
 * and by people with colour-vision deficiency, and these six are the ones the
 * design standards already validate for that. Each swatch carries its name, so
 * the choice is never made by colour alone.
 */
/**
 * What a note says, and whose it is.
 *
 * The author is shown and never editable: the server sets it from whoever
 * placed the note, and a field would invite a client to claim someone else's
 * words.
 */
function NoteFields({
    note,
    editable,
    onChange,
}: {
    note: NoteNode;
    editable: boolean;
    onChange: (note: NoteNode) => void;
}) {
    const { t } = useTranslation();

    return (
        <div className="space-y-2">
            <Field label={t("systemicMap.inspector.note")}>
                <textarea
                    value={note.text}
                    rows={5}
                    maxLength={MAX_NOTE_LENGTH}
                    disabled={!editable}
                    placeholder={t("systemicMap.inspector.notePlaceholder")}
                    onChange={(event) => onChange({ ...note, text: event.target.value })}
                    className={`${inputClass} min-h-24 py-2`}
                />
            </Field>
            {note.author && (
                <p className="text-xs text-content-secondary">
                    {t("systemicMap.note.by", { author: note.author })}
                </p>
            )}
        </div>
    );
}

/**
 * The home the selected person lives in.
 *
 * Shown from the person rather than from the boundary because that is how a
 * therapist asks it — "who does she live with?" — and because it is the only
 * place someone can be taken out of a home without deleting the home.
 */
function HouseholdFields({
    household,
    editable,
    onChange,
    onLeave,
}: {
    household: Household;
    editable: boolean;
    onChange?: (household: Household) => void;
    onLeave?: () => void;
}) {
    const { t } = useTranslation();

    return (
        <div className="space-y-2 rounded-lg border border-border-subtle p-3">
            <Field label={t("systemicMap.inspector.household")}>
                <input
                    type="text"
                    value={household.label}
                    maxLength={MAX_NAME_LENGTH}
                    disabled={!editable || !onChange}
                    placeholder={t("systemicMap.inspector.householdName")}
                    onChange={(event) =>
                        onChange?.({ ...household, label: event.target.value })
                    }
                    className={inputClass}
                />
            </Field>
            <p className="text-xs text-content-secondary">
                {t("systemicMap.inspector.householdMembers", {
                    count: household.members.length,
                })}
            </p>
            {editable && onLeave && (
                <button
                    type="button"
                    onClick={onLeave}
                    className="min-h-11 w-full rounded-lg border border-border-strong px-4 py-2.5 text-sm font-medium text-content-secondary transition-colors duration-fast hover:bg-surface-sunken focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                >
                    {t("systemicMap.inspector.leaveHousehold")}
                </button>
            )}
        </div>
    );
}

function ColourPicker({
    value,
    onChange,
}: {
    value: ElementColour;
    onChange: (colour: ElementColour) => void;
}) {
    const { t } = useTranslation();

    return (
        <fieldset>
            <legend className="mb-1 block text-[13px] font-medium text-content-secondary">
                {t("systemicMap.inspector.colour")}
            </legend>
            <div className="flex flex-wrap gap-1.5">
                {ELEMENT_COLOURS.map((colour) => {
                    const ink = COLOUR_INK[colour];
                    const label = t(`systemicMap.colour.${colour}`);
                    return (
                        <button
                            key={colour}
                            type="button"
                            aria-label={label}
                            aria-pressed={value === colour}
                            title={label}
                            onClick={() => onChange(colour)}
                            className={`flex h-11 w-11 items-center justify-center rounded-lg border transition-colors duration-fast focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent ${
                                value === colour
                                    ? "border-accent bg-accent-tint"
                                    : "border-border-subtle hover:bg-surface-sunken"
                            }`}
                        >
                            <span
                                aria-hidden="true"
                                className="h-4 w-4 rounded-full border border-border-strong"
                                style={{ background: ink ?? "currentColor" }}
                            />
                        </button>
                    );
                })}
            </div>
        </fieldset>
    );
}

/**
 * The selected relationship.
 *
 * A union carries dates as well as a kind, because that is how the notation
 * reads: a couple married in 1984 and divorced in 2010 is a different family
 * from one married last year. The dates belong to the union rather than to a
 * separate "divorce" element — one line, one history.
 */
function LinkFields({
    link,
    editable,
    onChange,
}: {
    link: MapLink;
    editable: boolean;
    onChange: (link: MapLink) => void;
}) {
    const { t } = useTranslation();

    if (link.type === "union") {
        return (
            <div className="space-y-3">
                <Field label={t("systemicMap.inspector.unionType")}>
                    <select
                        value={link.union}
                        disabled={!editable}
                        onChange={(event) =>
                            onChange({
                                ...link,
                                union: event.target.value as UnionType,
                            })
                        }
                        className={inputClass}
                    >
                        {UNION_TYPES.map((value) => (
                            <option key={value} value={value}>
                                {t(`systemicMap.union.${value}`)}
                            </option>
                        ))}
                    </select>
                </Field>

                <div className="grid grid-cols-2 gap-3">
                    <YearField
                        label={t("systemicMap.inspector.unionSince")}
                        value={link.since}
                        disabled={!editable}
                        onCommit={(since) => onChange({ ...link, since })}
                    />
                    <YearField
                        label={t("systemicMap.inspector.unionUntil")}
                        value={link.until}
                        disabled={!editable}
                        onCommit={(until) => onChange({ ...link, until })}
                    />
                </div>
                <p className="text-xs text-content-secondary">
                    {t("systemicMap.inspector.unionDatesHint")}
                </p>
            </div>
        );
    }

    if (link.type === "child") {
        return (
            <Field label={t("systemicMap.inspector.childType")}>
                <select
                    value={link.child}
                    disabled={!editable}
                    onChange={(event) =>
                        onChange({ ...link, child: event.target.value as ChildType })
                    }
                    className={inputClass}
                >
                    {CHILD_TYPES.map((value) => (
                        <option key={value} value={value}>
                            {t(`systemicMap.child.${value}`)}
                        </option>
                    ))}
                </select>
            </Field>
        );
    }

    if (link.type === "emotional") {
        return (
            <Field label={t("systemicMap.inspector.emotionalType")}>
                <select
                    value={link.emotional}
                    disabled={!editable}
                    onChange={(event) =>
                        onChange({
                            ...link,
                            emotional: event.target.value as EmotionalType,
                        })
                    }
                    className={inputClass}
                >
                    {EMOTIONAL_TYPES.map((value) => (
                        <option key={value} value={value}>
                            {t(`systemicMap.emotional.${value}`)}
                        </option>
                    ))}
                </select>
            </Field>
        );
    }

    return (
        <div className="space-y-3">
            <Field label={t("systemicMap.legend.strength")}>
                <select
                    value={link.strength}
                    disabled={!editable}
                    onChange={(event) =>
                        onChange({
                            ...link,
                            strength: event.target.value as LinkStrength,
                        })
                    }
                    className={inputClass}
                >
                    {LINK_STRENGTHS.map((value) => (
                        <option key={value} value={value}>
                            {t(`systemicMap.strength.${value}`)}
                        </option>
                    ))}
                </select>
            </Field>
            <Field label={t("systemicMap.legend.energy")}>
                <select
                    value={link.energy}
                    disabled={!editable}
                    onChange={(event) =>
                        onChange({
                            ...link,
                            energy: event.target.value as EnergyDirection,
                        })
                    }
                    className={inputClass}
                >
                    {ENERGY_DIRECTIONS.map((value) => (
                        <option key={value} value={value}>
                            {t(`systemicMap.energy.${value}`)}
                        </option>
                    ))}
                </select>
            </Field>
        </div>
    );
}

/**
 * A year, typed one digit at a time.
 *
 * The field keeps what is being typed and only commits a year the board can
 * hold. Bound straight to the element, "1968" travelled as 1, 19, 196 and
 * 1968 — and the first three are outside the accepted range, so the server
 * refused them and the board reported that it could not save while someone
 * was doing nothing more exotic than typing a date.
 */
function YearField({
    label,
    value,
    disabled,
    onCommit,
}: {
    label: string;
    value: number | null;
    disabled: boolean;
    onCommit: (year: number | null) => void;
}) {
    const [text, setText] = useState(value === null ? "" : String(value));

    // Follows the element when the other side changes it, but never while it
    // is being typed into.
    useEffect(() => {
        setText((current) => {
            const committed = value === null ? "" : String(value);
            return Number(current) === value || current === committed
                ? committed
                : current;
        });
    }, [value]);

    const commit = (raw: string) => {
        const trimmed = raw.trim();
        if (!trimmed) {
            onCommit(null);
            return;
        }
        const parsed = Number(trimmed);
        if (Number.isInteger(parsed) && parsed >= MIN_YEAR && parsed <= MAX_YEAR) {
            onCommit(parsed);
        }
        // Anything else is a year half typed. Kept on screen, not sent.
    };

    return (
        <Field label={label}>
            <input
                type="number"
                inputMode="numeric"
                min={MIN_YEAR}
                max={MAX_YEAR}
                value={text}
                disabled={disabled}
                onChange={(event) => {
                    setText(event.target.value);
                    commit(event.target.value);
                }}
                className={inputClass}
            />
        </Field>
    );
}

function SystemFields({
    system,
    editable,
    onChange,
}: {
    system: SystemNode;
    editable: boolean;
    onChange: (system: SystemNode) => void;
}) {
    const { t } = useTranslation();

    return (
        <div className="space-y-3">
            <Field label={t("systemicMap.inspector.systemLabel")}>
                <input
                    type="text"
                    value={system.label}
                    maxLength={MAX_NAME_LENGTH}
                    placeholder={t(`systemicMap.system.${system.system}`)}
                    disabled={!editable}
                    onChange={(event) => onChange({ ...system, label: event.target.value })}
                    className={inputClass}
                />
            </Field>
        </div>
    );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <label className="block">
            <span className="mb-1 block text-[13px] font-medium text-content-secondary">
                {label}
            </span>
            {children}
        </label>
    );
}

function Checkbox({
    label,
    checked,
    disabled,
    onChange,
}: {
    label: string;
    checked: boolean;
    disabled?: boolean;
    onChange: (checked: boolean) => void;
}) {
    return (
        <label className="flex min-h-11 items-center gap-2.5 text-sm text-content-primary">
            <input
                type="checkbox"
                checked={checked}
                disabled={disabled}
                onChange={(event) => onChange(event.target.checked)}
                className="h-5 w-5 rounded border-border-strong text-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
            />
            {label}
        </label>
    );
}
